import type { GearRules, Perk } from "@shared/entities";
import { HOOK_LABEL, SLOT_LABEL } from "@shared/game";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AnnotationEditor } from "../components/annotation-editor";
import { Implemented, RarityText } from "../components/chips";
import { LoadError, Loading, PageHeader } from "../components/page-state";
import { num, pct } from "../lib/format";
import { gameTableQuery, gearQuery } from "../lib/queries";

export const Route = createFileRoute("/gear/")({ component: GearPage });

const STAT_LABEL = { hp: "HP", atk: "ATK", def: "DEF" } as const;

function GearPage() {
	const gear = useQuery(gearQuery);
	const rules = useQuery(gameTableQuery("gear"));

	if (gear.isPending) return <Loading what="Loading gear" />;
	if (gear.isError) return <LoadError error={gear.error} retry={() => void gear.refetch()} />;

	const r = rules.data?.data as GearRules | undefined;

	return (
		<div className="space-y-8">
			<PageHeader title="Gear and perks" eyebrow="Six slots, six rarities, levels on the slot">
				<Link to="/gear/catalog" className="btn">
					Open the item catalog
				</Link>
			</PageHeader>
			<p className="max-w-2xl" style={{ color: "var(--color-ink-300)" }}>
				An item's stat is <code className="font-mono text-sm">base × rarity</code>; the slot's level multiplies it again and unlocks a
				perk every ten levels. Scrolls are never wasted when a better item replaces the old one.
			</p>

			{r && (
				<section className="panel p-5">
					<h2 className="mb-3 text-base">Rarity multipliers</h2>
					<div className="flex flex-wrap gap-x-6 gap-y-2">
						{r.rarity_order.map((rar) => (
							<div key={rar}>
								<RarityText rarity={rar} />
								<span className="num ml-2 text-sm" style={{ color: "var(--color-ink-300)" }}>
									×{r.rarity_multiplier[rar]}
								</span>
							</div>
						))}
					</div>
					<p className="mt-3 text-xs" style={{ color: "var(--color-ink-400)" }}>
						Slot level {r.slot_level.min}–{r.slot_level.max}: +{pct(r.slot_level.stat_per_level)} per level; scroll cost{" "}
						<code className="font-mono">
							ceil({r.slot_level.scroll_cost_base} × {r.slot_level.scroll_cost_growth}^level)
						</code>
						. Milestones at {r.slot_level.milestones.join(" / ")}.
					</p>
				</section>
			)}

			<div className="grid gap-6 lg:grid-cols-2">
				{gear.data.map((slot) => (
					<section key={slot.id} className="panel p-5">
						<header className="mb-3 flex items-baseline justify-between">
							<h2 className="text-lg">{SLOT_LABEL[slot.id]}</h2>
							<span className="text-sm" style={{ color: "var(--color-ink-300)" }}>
								{STAT_LABEL[slot.primary_stat]} <span className="num">{num(slot.base_stat)}</span> base
							</span>
						</header>
						<table className="table">
							<thead>
								<tr>
									<th className="num">Lv</th>
									<th>Perk</th>
									<th>Trigger</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{slot.perks.map((p) => (
									<tr key={p.id}>
										<td className="num">{p.level}</td>
										<td>{perkText(p)}</td>
										<td className="text-xs" style={{ color: "var(--color-ink-300)" }}>
											{HOOK_LABEL[p.hook]}
										</td>
										<td>
											<Implemented value={p.implemented} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
						<div className="mt-4 border-t pt-4" style={{ borderColor: "var(--color-ink-700)" }}>
							<AnnotationEditor type="gear_slot" id={slot.id} annotation={slot.annotation} fields={["notes"]} compact />
						</div>
					</section>
				))}
			</div>
		</div>
	);
}

/** Human text for a perk row, from its effect type and value. Mirrors DESIGN.md §7's table. */
function perkText(p: Perk): string {
	const v = pct(p.value);
	const stat = String(p.params["stat"] ?? "").toUpperCase();
	switch (p.effect_type) {
		case "crit":
			return `+${v} crit chance`;
		case "crit_damage":
			return `+${v} crit damage`;
		case "lifesteal":
			return `${v} lifesteal`;
		case "double_hit":
			return `${v} chance to hit twice`;
		case "flat_stat_percent":
			return `+${v} ${stat}`;
		case "damage_reduction":
			return `−${v} damage taken`;
		case "shield_on_hit":
			return `Shield for ${v} of damage taken`;
		case "thorns":
			return `Reflect ${v} of attacker ATK`;
		case "dodge":
			return `${v} dodge`;
		case "coin_bonus":
			return `+${v} coins`;
		case "xp_bonus":
			return `+${v} XP`;
		case "scroll_drop_bonus":
			return `+${v} scroll drops`;
		default:
			return `${p.effect_type} ${v}`;
	}
}
